import {
  AngebotSchema,
  AnfrageKonsolidiertSchema,
  ArtikelMatchSchema,
  GlobalArtikelSchema,
  GlobalCatalogSchema,
  HilfsartikelListSchema,
  HilfsartikelSchema,
  KlaerpunkteSchema,
  KonditionV2Schema,
  KonditionenV2Schema,
  KonditionsmatrixSchema,
  KundeSchema,
  KundenListeSchema,
  KundenartikelListSchema,
  KundenartikelSchema,
  MengenSchema,
  PreiseSchema,
  QsSchema,
  ServiceCapRecommendationSchema,
  TenantConfigSchema,
  TenantSortimentSchema,
  type Angebot,
  type AnfrageKonsolidiert,
  type AnfrageRohdaten,
  type ArtikelMatch,
  type GlobalArtikel,
  type GlobalCatalog,
  type Hilfsartikel,
  type HilfsartikelList,
  type KonditionV2,
  type KonditionenV2,
  type Klaerpunkte,
  type Konditionsmatrix,
  type Kunde,
  type KundenListe,
  type Kundenartikel,
  type KundenartikelList,
  type Mengen,
  type MengenRoom,
  type Preise,
  type Qs,
  type Schnellkalkulation,
  type ServiceCapRecommendation,
  type TenantConfig,
  type TenantSortiment,
} from "@bidi-handel/core/schemas";
import { z } from "zod";

/**
 * Type-safe API-Client gegen das bidi-handel Backend.
 *
 * Re-Use der Zod-Schemas aus @bidi-handel/core gibt uns End-to-End-Typen
 * ohne Schema-Duplikat. Backend-Routes liefern bereits gegen diese
 * Schemas validierte JSON-Antworten.
 *
 * Alle Calls gehen ueber relative `/api/...`-Pfade — Vite-Proxy in dev,
 * gleicher Reverse-Proxy in production.
 */

const AnfrageListItemSchema = z.object({
  anfrageId: z.string(),
  kunde: z.string(),
  bauvorhaben: z.string(),
  status: z.string(),
  pfad: z.string().nullable(),
  eingangsDatum: z.string(),
});
export type AnfrageListItem = z.infer<typeof AnfrageListItemSchema>;

const AnfrageListResponseSchema = z.object({
  anfragen: z.array(AnfrageListItemSchema),
});

const AnfrageDetailResponseSchema = z.object({
  rohdaten: z.unknown(),
  phasenVorhanden: z.array(z.number().int().nonnegative()),
});
export interface AnfrageDetailResponse {
  rohdaten: AnfrageRohdaten;
  phasenVorhanden: number[];
}

const StammdatenSummarySchema = z.object({
  artikel: z.object({
    gesamt: z.number().int().nonnegative(),
    warengruppen: z.array(
      z.object({ name: z.string(), count: z.number().int().nonnegative() })
    ),
    /** Catalog/Sortiment-Modus aktiv vs Legacy-kundenartikel.json. Default
     *  "legacy" damit alte Backend-Responses ohne Feld weiter parsen. */
    source: z.enum(["catalog", "legacy", "empty"]).default("legacy"),
  }),
  konditionen: z.object({
    gesamt: z.number().int().nonnegative(),
    kundenIds: z.array(z.string()),
    gefiltert: z
      .object({
        kundeId: z.string(),
        rabatte: z.number().int().nonnegative(),
        sonderpreise: z.number().int().nonnegative(),
        warengruppenRabatte: z.array(
          z.object({
            warengruppe: z.string(),
            rabattProzent: z.number(),
          })
        ),
        artikelSonderpreise: z.array(
          z.object({ artikelCode: z.string(), sonderpreis: z.number() })
        ),
      })
      .optional(),
  }),
  tenant: z.object({
    margeProzent: z.number(),
    waehrung: z.string(),
    firmaName: z.string().nullable(),
  }),
  lastModified: z.object({
    kundenartikel: z.string().nullable(),
    konditionsmatrix: z.string().nullable(),
    config: z.string().nullable(),
  }),
});
export type StammdatenSummary = z.infer<typeof StammdatenSummarySchema>;

const PhaseSummarySchema = z.object({
  phase: z.number().int(),
  slug: z.string(),
  status: z.enum(["ok", "skipped", "failed", "not_implemented"]),
  durationMs: z.number(),
  message: z.string().optional(),
});
const RunPipelineResultSchema = z.object({
  summaries: z.array(PhaseSummarySchema),
  klaerpunkteOffen: z.number().int().nonnegative(),
  pfad: z.enum(["A", "B"]).nullable(),
});
export type RunPipelineResultResponse = z.infer<typeof RunPipelineResultSchema>;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    public readonly url: string
  ) {
    super(
      `API ${status} bei ${url}: ${
        typeof body === "object" && body && "error" in body
          ? String((body as { error: unknown }).error)
          : "unknown error"
      }`
    );
    this.name = "ApiError";
  }
}

async function request<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  init?: RequestInit
): Promise<z.infer<S>> {
  const url = path.startsWith("/") ? path : `/${path}`;
  const res = await fetch(url, init);
  const text = await res.text();
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      throw new ApiError(res.status, { error: "non_json_response", text }, url);
    }
  }
  if (!res.ok) throw new ApiError(res.status, body, url);
  return schema.parse(body) as z.infer<S>;
}

const HealthSchema = z.object({ ok: z.boolean(), workspaceRoot: z.string() });

export const api = {
  health: () => request("/api/health", HealthSchema),

  listAnfragen: async (tenantId: string): Promise<AnfrageListItem[]> => {
    const r = await request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen`,
      AnfrageListResponseSchema
    );
    return r.anfragen;
  },

  getAnfrage: async (
    tenantId: string,
    anfrageId: string
  ): Promise<AnfrageDetailResponse> => {
    const raw = await request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}`,
      AnfrageDetailResponseSchema
    );
    return {
      rohdaten: raw.rohdaten as AnfrageRohdaten,
      phasenVorhanden: raw.phasenVorhanden,
    };
  },

  getAngebotManifest: (tenantId: string, anfrageId: string): Promise<Angebot> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/angebot/manifest`,
      AngebotSchema
    ),

  getMatch: (tenantId: string, anfrageId: string): Promise<ArtikelMatch> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/phase/4`,
      ArtikelMatchSchema
    ),

  getKonsolidiert: (
    tenantId: string,
    anfrageId: string
  ): Promise<AnfrageKonsolidiert> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/phase/3`,
      AnfrageKonsolidiertSchema
    ),

  getPreise: (tenantId: string, anfrageId: string): Promise<Preise> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/phase/5`,
      PreiseSchema
    ),

  getKlaerpunkte: (tenantId: string, anfrageId: string): Promise<Klaerpunkte> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/phase/97`,
      KlaerpunkteSchema
    ),

  /** Phase 98 — deterministischer QS-Bericht (Ampel/Coverage/Hinweise). */
  getQs: (tenantId: string, anfrageId: string): Promise<Qs> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/phase/98`,
      QsSchema
    ),

  resolveKlaerpunkt: (
    tenantId: string,
    anfrageId: string,
    klaerpunktId: string,
    body: { resolved: boolean; resolution?: string }
  ): Promise<Klaerpunkte> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/klaerpunkt/${encodeURIComponent(klaerpunktId)}`,
      KlaerpunkteSchema,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    ),

  // Bulk-Resolve: alle Klaerpunkte zu einer Position auf einmal. Backend
  // returnt { klaerpunkte, changed }. Wir geben nur klaerpunkte zurueck —
  // Caller braucht den count meist nicht.
  bulkResolveKlaerpunkteByPosition: (
    tenantId: string,
    anfrageId: string,
    positionId: string,
    body: { resolved: boolean; resolution?: string }
  ): Promise<Klaerpunkte> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/klaerpunkte/by-position/${encodeURIComponent(positionId)}`,
      z.object({
        klaerpunkte: KlaerpunkteSchema,
        changed: z.number().int().nonnegative(),
      }),
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    ).then((r) => r.klaerpunkte),

  patchPreis: (
    tenantId: string,
    anfrageId: string,
    positionId: string,
    patch: {
      rabattProzent?: number;
      sonderpreis?: number;
      margeProzent?: number;
    }
  ): Promise<Preise> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/preise/${encodeURIComponent(positionId)}`,
      PreiseSchema,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }
    ),

  setAnfrageStatus: (
    tenantId: string,
    anfrageId: string,
    status: string
  ): Promise<AnfrageRohdaten> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/status`,
      // Backend gibt das volle AnfrageRohdaten zurueck — wir validieren
      // hier nur leicht und unwrap'n im Detail-Page-State.
      z.object({ status: z.string() }).passthrough() as unknown as z.ZodType<AnfrageRohdaten>,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      }
    ),

  patchDateiKlassifikation: (
    tenantId: string,
    anfrageId: string,
    body: { relPath: string; erkanntAls: "lv" | "bauplan" | "unklar"; bestaetigt?: boolean }
  ): Promise<AnfrageRohdaten> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/datei-klassifikation`,
      z.object({}).passthrough() as unknown as z.ZodType<AnfrageRohdaten>,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    ),

  pickMatch: (
    tenantId: string,
    anfrageId: string,
    positionId: string,
    artikelCode: string | null
  ): Promise<ArtikelMatch> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/match/${encodeURIComponent(positionId)}`,
      ArtikelMatchSchema,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ gewaehlterArtikel: artikelCode }),
      }
    ),

  setNichtAngeboten: (
    tenantId: string,
    anfrageId: string,
    positionId: string,
    nichtAngeboten: boolean
  ): Promise<ArtikelMatch> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/match/${encodeURIComponent(positionId)}`,
      ArtikelMatchSchema,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nichtAngeboten }),
      }
    ),

  // ──────────────────────────────────────────────────────────────────
  // Mengen-Editor (Pfad B, Slice 3)
  // ──────────────────────────────────────────────────────────────────

  getMengen: (tenantId: string, anfrageId: string): Promise<Mengen> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/mengen`,
      MengenSchema
    ),

  patchMengenScale: (
    tenantId: string,
    anfrageId: string,
    body:
      | { planIndex?: number; ratio: number }
      | {
          planIndex?: number;
          p1: { x: number; y: number };
          p2: { x: number; y: number };
          realMeters: number;
        }
  ): Promise<Mengen> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/mengen/scale`,
      MengenSchema,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    ),

  patchMengenPolygon: (
    tenantId: string,
    anfrageId: string,
    polygonId: string,
    body: { planIndex?: number; coordinates: number[][][] }
  ): Promise<Mengen> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/mengen/polygons/${encodeURIComponent(polygonId)}`,
      MengenSchema,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    ),

  recomputeMengen: (
    tenantId: string,
    anfrageId: string,
    body?: { raumhoeheM?: number }
  ): Promise<{ mengen: Mengen; vorLvPositionen: number }> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/mengen/recompute`,
      z.object({ mengen: MengenSchema, vorLvPositionen: z.number().int().nonnegative() }),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body ?? {}),
      }
    ),

  // Neues Raum-Polygon manuell anlegen (P2b/P3).
  addMengenPolygon: (
    tenantId: string,
    anfrageId: string,
    body: {
      planIndex?: number;
      coordinates: number[][][];
      label?: string;
      din277?: MengenRoom["din277"];
      bodenflaecheM2?: number;
    }
  ): Promise<{ mengen: Mengen; roomId: string }> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/mengen/polygons`,
      z.object({ mengen: MengenSchema, roomId: z.string() }),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    ),

  // Raum-Polygon löschen (P2b).
  deleteMengenPolygon: (
    tenantId: string,
    anfrageId: string,
    polygonId: string,
    body?: { planIndex?: number }
  ): Promise<Mengen> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/mengen/polygons/${encodeURIComponent(polygonId)}`,
      MengenSchema,
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body ?? {}),
      }
    ),

  // Raum-Eigenschaften patchen: Bemusterung (P4), Wände (P2c), Label,
  // Flächen-Override/Reset (P3/P1).
  patchMengenRoom: (
    tenantId: string,
    anfrageId: string,
    roomId: string,
    body: {
      planIndex?: number;
      label?: string;
      bemusterung?: MengenRoom["bemusterung"];
      walls?: MengenRoom["walls"];
      bodenflaecheM2?: number;
      bodenQuelleReset?: boolean;
    }
  ): Promise<Mengen> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/mengen/rooms/${encodeURIComponent(roomId)}`,
      MengenSchema,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    ),

  /**
   * Weist mehreren Räumen eine Wohneinheit zu (#5A). `wohneinheit: null` oder
   * "" entfernt die Zuordnung. Rein organisatorisch — kein Recompute/Downstream.
   */
  patchMengenWohneinheit: (
    tenantId: string,
    anfrageId: string,
    body: { planIndex?: number; roomIds: string[]; wohneinheit: string | null }
  ): Promise<{ mengen: Mengen; changed: number }> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/mengen/wohneinheit`,
      z.object({ mengen: MengenSchema, changed: z.number().int().nonnegative() }),
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    ),

  /** Auto-Vorbelegung der Wohneinheiten aus „WE N"-Plan-Stempeln (#5A). */
  autoMengenWohneinheit: (
    tenantId: string,
    anfrageId: string,
    planIndex = 0
  ): Promise<{
    mengen: Mengen;
    assigned: number;
    wohneinheiten: Array<{ weId: string; raeume: number; ngfM2: number }>;
  }> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/mengen/wohneinheit/auto`,
      z.object({
        mengen: MengenSchema,
        assigned: z.number().int().nonnegative(),
        wohneinheiten: z.array(
          z.object({
            weId: z.string(),
            raeume: z.number().int().nonnegative(),
            ngfM2: z.number(),
          })
        ),
      }),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planIndex }),
      }
    ),

  /**
   * Setzt oder entfernt die Schnellkalkulation einer Position. `null` als
   * Wert entfernt eine bestehende Schnellkalk und faellt zurueck auf
   * „Kein Pick" — User muss neu entscheiden.
   *
   * Mutually exclusive zu `pickMatch`/`setNichtAngeboten` (Backend setzt
   * das durch).
   */
  setSchnellkalkulation: (
    tenantId: string,
    anfrageId: string,
    positionId: string,
    schnellkalkulation: Schnellkalkulation | null
  ): Promise<ArtikelMatch> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/match/${encodeURIComponent(positionId)}`,
      ArtikelMatchSchema,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schnellkalkulation }),
      }
    ),

  getKundenartikel: (tenantId: string): Promise<KundenartikelList> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/stammdaten/kundenartikel`,
      KundenartikelListSchema
    ),

  getHilfsartikel: (tenantId: string): Promise<HilfsartikelList> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/stammdaten/hilfsartikel`,
      HilfsartikelListSchema
    ),

  createHilfsartikel: (
    tenantId: string,
    hilfsartikel: Hilfsartikel
  ): Promise<Hilfsartikel> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/stammdaten/hilfsartikel`,
      HilfsartikelSchema,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(hilfsartikel),
      }
    ),

  // ──────────────────────────────────────────────────────────────────
  // Global-Catalog + Tenant-Sortiment (Sprint G1)
  // ──────────────────────────────────────────────────────────────────

  getCatalog: (): Promise<GlobalCatalog> =>
    request("/api/catalog/artikel", GlobalCatalogSchema),

  putCatalog: (catalog: GlobalCatalog): Promise<GlobalCatalog> =>
    request("/api/catalog/artikel", GlobalCatalogSchema, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(catalog),
    }),

  createCatalogArtikel: (artikel: GlobalArtikel): Promise<GlobalArtikel> =>
    request("/api/catalog/artikel", GlobalArtikelSchema, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(artikel),
    }),

  /** Batch-Import: nimmt eine Liste schmaler CSV-Zeilen, upsertet sie per
   *  `code` in den Catalog und (optional) ins Sortiment des angegebenen
   *  Tenants. Konflikt-Strategie waehlbar (update = CSV ueberschreibt;
   *  skip = bestehender Eintrag bleibt). Antwort = Report fuer das UI. */
  importCatalog: (payload: {
    artikel: Array<{
      code: string;
      bezeichnung: string;
      kurzText?: string;
      ean?: string;
      vpe?: string;
      einheit?: string;
      warengruppe?: string;
      listenpreis?: number;
      suchbegriffe?: string[];
    }>;
    onConflict: "update" | "skip";
    addToSortimentForTenant?: string;
  }): Promise<{
    created: number;
    updated: number;
    skipped: Array<{ code: string; reason: string }>;
    sortimentAdded: number;
    totalProcessed: number;
  }> =>
    request(
      "/api/catalog/import",
      z.object({
        created: z.number().int().nonnegative(),
        updated: z.number().int().nonnegative(),
        skipped: z.array(
          z.object({ code: z.string(), reason: z.string() })
        ),
        sortimentAdded: z.number().int().nonnegative(),
        totalProcessed: z.number().int().nonnegative(),
      }),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    ),

  patchCatalogArtikel: (
    id: string,
    patch: Partial<GlobalArtikel>
  ): Promise<GlobalArtikel> =>
    request(
      `/api/catalog/artikel/${encodeURIComponent(id)}`,
      GlobalArtikelSchema,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }
    ),

  deleteCatalogArtikel: (id: string): Promise<GlobalArtikel> =>
    request(
      `/api/catalog/artikel/${encodeURIComponent(id)}`,
      GlobalArtikelSchema,
      { method: "DELETE" }
    ),

  getSortiment: (
    tenantId: string
  ): Promise<{
    sortiment: TenantSortiment | null;
    effective: Kundenartikel[];
    orphanRefs: string[];
    legacyAvailable: boolean;
  }> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/sortiment`,
      z.object({
        sortiment: TenantSortimentSchema.nullable(),
        effective: z.array(KundenartikelSchema),
        orphanRefs: z.array(z.string()),
        legacyAvailable: z.boolean(),
      })
    ),

  putSortiment: (
    tenantId: string,
    sortiment: TenantSortiment
  ): Promise<TenantSortiment> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/sortiment`,
      TenantSortimentSchema,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sortiment),
      }
    ),

  migrateLegacy: (
    tenantId: string,
    opts?: { archiveLegacy?: boolean }
  ): Promise<{
    catalogAdded: number;
    catalogReused: number;
    sortimentSize: number;
    legacyArchive: string | null;
  }> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/migrate-legacy`,
      z.object({
        catalogAdded: z.number().int().nonnegative(),
        catalogReused: z.number().int().nonnegative(),
        sortimentSize: z.number().int().nonnegative(),
        legacyArchive: z.string().nullable(),
      }),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts ?? {}),
      }
    ),

  // ──────────────────────────────────────────────────────────────────
  // Kunden + Konditionen v2 (Sprint G2)
  // ──────────────────────────────────────────────────────────────────

  getKunden: (tenantId: string): Promise<KundenListe> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/kunden`,
      KundenListeSchema
    ),

  createKunde: (tenantId: string, kunde: Kunde): Promise<Kunde> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/kunden`,
      KundeSchema,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(kunde),
      }
    ),

  patchKunde: (
    tenantId: string,
    kundeId: string,
    patch: Partial<Kunde>
  ): Promise<Kunde> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/kunden/${encodeURIComponent(kundeId)}`,
      KundeSchema,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }
    ),

  deleteKunde: (tenantId: string, kundeId: string): Promise<Kunde> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/kunden/${encodeURIComponent(kundeId)}`,
      KundeSchema,
      { method: "DELETE" }
    ),

  getKonditionenV2: (
    tenantId: string,
    filter?: {
      kundeId?: string;
      artikelCode?: string;
      warengruppe?: string;
    }
  ): Promise<KonditionenV2> => {
    const q = new URLSearchParams();
    if (filter?.kundeId) q.set("kundeId", filter.kundeId);
    if (filter?.artikelCode) q.set("artikelCode", filter.artikelCode);
    if (filter?.warengruppe) q.set("warengruppe", filter.warengruppe);
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return request(
      `/api/tenants/${encodeURIComponent(tenantId)}/konditionen${suffix}`,
      KonditionenV2Schema
    );
  },

  createKondition: (
    tenantId: string,
    kondition: KonditionV2
  ): Promise<KonditionV2> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/konditionen`,
      KonditionV2Schema,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(kondition),
      }
    ),

  patchKondition: (
    tenantId: string,
    konditionId: string,
    patch: Partial<KonditionV2>
  ): Promise<KonditionV2> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/konditionen/${encodeURIComponent(konditionId)}`,
      KonditionV2Schema,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }
    ),

  deleteKondition: async (
    tenantId: string,
    konditionId: string
  ): Promise<void> => {
    const url = `/api/tenants/${encodeURIComponent(tenantId)}/konditionen/${encodeURIComponent(konditionId)}`;
    const res = await fetch(url, { method: "DELETE" });
    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      throw new ApiError(res.status, body, url);
    }
  },

  getKonditionsmatrix: (tenantId: string): Promise<Konditionsmatrix> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/stammdaten/konditionsmatrix`,
      KonditionsmatrixSchema
    ),

  getTenantConfig: (tenantId: string): Promise<TenantConfig> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/config`,
      TenantConfigSchema
    ),

  putKundenartikel: (
    tenantId: string,
    list: KundenartikelList
  ): Promise<KundenartikelList> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/stammdaten/kundenartikel`,
      KundenartikelListSchema,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(list),
      }
    ),

  putKonditionsmatrix: (
    tenantId: string,
    matrix: Konditionsmatrix
  ): Promise<Konditionsmatrix> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/stammdaten/konditionsmatrix`,
      KonditionsmatrixSchema,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(matrix),
      }
    ),

  putTenantConfig: (
    tenantId: string,
    config: TenantConfig
  ): Promise<TenantConfig> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/config`,
      TenantConfigSchema,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(config),
      }
    ),

  getMatchPolicyStats: (
    tenantId: string
  ): Promise<ServiceCapRecommendation> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/match-policy/stats`,
      ServiceCapRecommendationSchema
    ),

  getStammdatenSummary: (
    tenantId: string,
    kundeId?: string
  ): Promise<StammdatenSummary> => {
    const q = kundeId ? `?kundeId=${encodeURIComponent(kundeId)}` : "";
    return request(
      `/api/tenants/${encodeURIComponent(tenantId)}/stammdaten/summary${q}`,
      StammdatenSummarySchema
    );
  },

  runPipeline: (
    tenantId: string,
    anfrageId: string,
    opts: { untilPhase?: number; autoPickMatches?: boolean; resume?: boolean }
  ): Promise<RunPipelineResultResponse> =>
    request(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/run`,
      RunPipelineResultSchema,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts),
      }
    ),

  /** Direkter Download-URL (kein fetch — Browser laedt selbst). */
  angebotDownloadUrl: (
    tenantId: string,
    anfrageId: string,
    format: "xlsx" | "pdf"
  ): string =>
    `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/angebot?format=${format}`,

  /**
   * URL fuer den File-Stream einer hochgeladenen Original-Datei aus dem
   * eingang/-Verzeichnis. Nutzbar als `<a href>` (Download) oder als
   * pdfjs-Document-Source.
   *
   * `relPath` darf bereits "eingang/..." enthalten oder nur den Datei-
   * Pfad relativ zum eingang-Dir. Beides wird unterstuetzt — wir
   * normalisieren auf den Datei-Pfad.
   */
  eingangFileUrl: (
    tenantId: string,
    anfrageId: string,
    relPath: string
  ): string => {
    // Falls relPath schon das `.bidi-handel/tenants/.../eingang/`-Prefix
    // hat (so liefert es das Backend in DateiRef.relPath), strippen wir
    // alles bis "eingang/" weg.
    const m = relPath.match(/eingang\/(.+)$/);
    const fileRel = m ? m[1]! : relPath;
    const parts = fileRel.split("/").map(encodeURIComponent).join("/");
    return `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(anfrageId)}/eingang/${parts}`;
  },
};
